import { useEffect, useState } from 'react';
import { v1, toResult } from '@/infrastructure/api/v1/client';

export interface PublishFlowProject {
  name: string;
  description: string;
  tags: string[];
}

// Fetches the project's name/description/tags from the DB so the publish flow is
// populated from any entry point (the editor TopMenu has no project loaded otherwise).
export function usePublishFlowProject(projectInstanceId: string | null | undefined, enabled: boolean): PublishFlowProject | null {
  const [project, setProject] = useState<PublishFlowProject | null>(null);

  // Disabling, or losing the project id, clears what was read
  const active = enabled && !!projectInstanceId;
  const [wasActive, setWasActive] = useState(active);
  if (wasActive !== active) {
    setWasActive(active);
    if (!active) setProject(null);
  }

  useEffect(() => {
    if (!enabled || !projectInstanceId) return;
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await toResult(v1.GET('/api/v1/projects/{projectUniqueId}', {
          params: { path: { projectUniqueId: projectInstanceId } },
        }));
        if (cancelled) return;
        setProject({
          name: data?.displayName ?? '',
          description: data?.description ?? '',
          tags: data?.tags ?? [],
        });
      } catch {
        if (!cancelled) setProject(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, projectInstanceId]);

  return project;
}
